import { useId, useMemo } from 'react';
import { ALL_ACCOUNTS, useAccount, type AccountSelection } from '../contexts/AccountContext';
import { useI18n } from '../i18n/I18nProvider';
import { dedupeAccountsByName } from '../lib/portfolio';

/**
 * Account scope control shown at the top of every page.
 *
 * One linked account → a static label. Several → a native <select> with an
 * "All accounts" (portfolio) entry plus one entry per account. A native
 * control is used deliberately: it is keyboard/screen-reader complete and a
 * full-height tap target on phones without any custom menu code.
 */
/**
 * `status` renders only what the page must say (loading, an error, no linked
 * account) and nothing once accounts are known. `compact` is the top-bar
 * control: a small select, or the account name when there is only one.
 * Splitting them removed a full-width bar from the top of every page that
 * repeated the account the top bar already showed.
 */
export function AccountSwitcher({ variant = 'status' }: { variant?: 'status' | 'compact' }) {
  const { accounts, selection, isPortfolio, activeAccount, setSelection, loading, ready, error } =
    useAccount();
  const { t } = useI18n();
  const selectId = useId();
  // Zoho sometimes carries several customer rows for one property (for
  // example a POS-only account). Collapse them for display; the query scope
  // still covers every linked row, and the active account is always kept.
  const visibleAccounts = useMemo(
    () => dedupeAccountsByName(accounts, activeAccount?.customer_id),
    [accounts, activeAccount?.customer_id],
  );

  if (variant === 'compact') {
    if (loading || !ready || error || accounts.length === 0) return null;
    if (visibleAccounts.length === 1) {
      return (
        <span className="max-w-[140px] truncate text-xs font-medium text-text sm:max-w-[220px]" title={visibleAccounts[0].name}>
          {visibleAccounts[0].name}
        </span>
      );
    }
    return (
      <>
        <label htmlFor={selectId} className="sr-only">
          {t('account.label')}
        </label>
        <select
          id={selectId}
          value={selection}
          onChange={(event) => setSelection(event.target.value as AccountSelection)}
          title={isPortfolio ? t('account.portfolio_hint') : activeAccount?.name}
          className="min-h-11 w-[140px] truncate rounded-md border border-border bg-surface px-2 text-xs font-medium text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:min-h-8 sm:w-[240px]"
        >
          <option value={ALL_ACCOUNTS}>{t('account.all_count', { n: visibleAccounts.length })}</option>
          {visibleAccounts.map((account) => (
            <option key={account.customer_id} value={account.customer_id}>
              {account.name}
            </option>
          ))}
        </select>
      </>
    );
  }

  if (loading || !ready) {
    return (
      <div className="border-b border-border bg-bg-elevated px-3 py-2 text-xs text-text-muted sm:px-4">
        {t('common.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div
        role="alert"
        className="border-b border-notification bg-notification-soft px-3 py-2 text-xs text-notification sm:px-4"
      >
        {t('account.error')} {error.message}
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="border-b border-border bg-bg-elevated px-3 py-2 text-xs text-text-muted sm:px-4">
        <span className="font-medium text-text">{t('account.none')}</span> {t('account.none_hint')}
      </div>
    );
  }

  // Accounts are known: the top-bar control shows them.
  return null;
}
